import { EditOutlined, PlusOutlined, ReloadOutlined, WarningOutlined } from "@ant-design/icons";
import { Box, IconButton } from "@mui/material";
import { Alert, Divider, Form, Modal, Space, Switch, Tag, Tooltip, Typography, message } from "antd";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  CountryPaymentGateway,
  CountryPaymentGatewayRequest,
  PaymentGateway,
  PaymentGatewayRequest,
} from "../../../@type/billing.types";
import { BillingService } from "../../../api/billingService";
import Card from "../../../components/common/Card";
import ActionButton from "../../../components/common/buttons/ActionButton";
import CancelButton from "../../../components/common/buttons/CancelButton";
import InputField from "../../../components/common/fields/InputField";
import SelectField from "../../../components/common/fields/SelectField";
import StatusBadge from "../../../components/common/StatusBadge";
import MuiTable from "../../../components/common/MuiTable";
import { useCountries } from "../../../hooks/useCountries";
import { Column } from "../../../utils/constants";

const unwrap = (response: any) => response?.data?.data ?? response?.data ?? [];

const IMPLEMENTED_PROCESSORS = new Set(["STRIPE", "VNPAY", "SEPAY"]);

const gatewayTypes = ["CARD", "WALLET", "BANK_TRANSFER", "QR", "GATEWAY"].map((type) => ({
  label: type,
  value: type,
}));

const defaultGatewayValues: PaymentGatewayRequest = {
  code: "",
  name: "",
  type: "GATEWAY",
  description: "",
  icon: "",
  checkout_url: "",
  is_active: true,
  is_default: false,
  display_order: 0,
};

const defaultCountryGatewayValues: CountryPaymentGatewayRequest = {
  country_id: undefined as unknown as number,
  payment_gateway_id: undefined as unknown as number,
  is_enabled: true,
  is_primary: false,
  display_order: 0,
};

interface ModalFooterProps {
  onCancel: () => void;
  onSave: () => void;
  saving: boolean;
}

const ModalFooter = ({ onCancel, onSave, saving }: ModalFooterProps) => (
  <Box display="flex" justifyContent="flex-end" gap={1.5}>
    <CancelButton onClick={onCancel}>Cancel</CancelButton>
    <ActionButton onClick={onSave} loading={saving}>
      Save
    </ActionButton>
  </Box>
);

const PaymentGatewayConfig = () => {
  const [gatewayForm] = Form.useForm<PaymentGatewayRequest>();
  const [countryGatewayForm] = Form.useForm<CountryPaymentGatewayRequest>();
  const [messageApi, contextHolder] = message.useMessage();
  const [gateways, setGateways] = useState<PaymentGateway[]>([]);
  const [countryGateways, setCountryGateways] = useState<CountryPaymentGateway[]>([]);
  const [loading, setLoading] = useState(false);
  const [savingGateway, setSavingGateway] = useState(false);
  const [savingCountryGateway, setSavingCountryGateway] = useState(false);
  const [gatewayModalOpen, setGatewayModalOpen] = useState(false);
  const [countryGatewayModalOpen, setCountryGatewayModalOpen] = useState(false);
  const [editingGateway, setEditingGateway] = useState<PaymentGateway | null>(null);
  const [editingCountryGateway, setEditingCountryGateway] = useState<CountryPaymentGateway | null>(null);
  const [{ data: countries }] = useCountries();

  const countryOptions = useMemo(
    () =>
      countries
        .filter((country) => country.active)
        .map((country) => ({
          label: `${country.name} (${country.code})`,
          value: country.id,
        })),
    [countries]
  );

  const gatewayOptions = useMemo(
    () =>
      gateways.map((gateway) => ({
        label: `${gateway.name} (${gateway.code})`,
        value: gateway.id,
      })),
    [gateways]
  );

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [gatewayResponse, countryGatewayResponse] = await Promise.all([
        BillingService.getPaymentGateways(),
        BillingService.getCountryPaymentGateways(),
      ]);
      setGateways(unwrap(gatewayResponse));
      setCountryGateways(unwrap(countryGatewayResponse));
    } catch (error) {
      messageApi.error("Failed to load payment gateway config");
    } finally {
      setLoading(false);
    }
  }, [messageApi]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const openCreateGateway = () => {
    setEditingGateway(null);
    gatewayForm.setFieldsValue(defaultGatewayValues);
    setGatewayModalOpen(true);
  };

  const openEditGateway = (gateway: PaymentGateway) => {
    setEditingGateway(gateway);
    gatewayForm.setFieldsValue({
      code: gateway.code,
      name: gateway.name,
      type: gateway.type,
      description: gateway.description || "",
      icon: gateway.icon || "",
      checkout_url: gateway.checkout_url || "",
      is_active: gateway.is_active,
      is_default: gateway.is_default,
      display_order: gateway.display_order,
    });
    setGatewayModalOpen(true);
  };

  const saveGateway = async () => {
    try {
      const values = await gatewayForm.validateFields();
      setSavingGateway(true);
      const payload = {
        ...values,
        code: values.code.toUpperCase(),
        type: values.type.toUpperCase(),
        display_order: Number(values.display_order || 0),
        is_active: Boolean(values.is_active),
        is_default: Boolean(values.is_default),
      };
      if (editingGateway) {
        await BillingService.updatePaymentGateway(editingGateway.id, payload);
      } else {
        await BillingService.createPaymentGateway(payload);
      }
      messageApi.success("Payment gateway saved");
      setGatewayModalOpen(false);
      await loadData();
    } catch (error: any) {
      if (!error?.errorFields) {
        messageApi.error("Failed to save payment gateway");
      }
    } finally {
      setSavingGateway(false);
    }
  };

  const openCreateCountryGateway = () => {
    setEditingCountryGateway(null);
    countryGatewayForm.setFieldsValue(defaultCountryGatewayValues);
    setCountryGatewayModalOpen(true);
  };

  const openEditCountryGateway = (mapping: CountryPaymentGateway) => {
    setEditingCountryGateway(mapping);
    countryGatewayForm.setFieldsValue({
      country_id: mapping.country_id,
      payment_gateway_id: mapping.payment_gateway_id,
      is_enabled: mapping.is_enabled,
      is_primary: mapping.is_primary,
      display_order: mapping.display_order,
    });
    setCountryGatewayModalOpen(true);
  };

  const saveCountryGateway = async () => {
    try {
      const values = await countryGatewayForm.validateFields();
      setSavingCountryGateway(true);
      const payload = {
        ...values,
        display_order: Number(values.display_order || 0),
        is_enabled: Boolean(values.is_enabled),
        is_primary: Boolean(values.is_primary),
      };
      if (editingCountryGateway) {
        await BillingService.updateCountryPaymentGateway(editingCountryGateway.id, payload);
      } else {
        await BillingService.createCountryPaymentGateway(payload);
      }
      messageApi.success("Country gateway assignment saved");
      setCountryGatewayModalOpen(false);
      await loadData();
    } catch (error: any) {
      if (!error?.errorFields) {
        messageApi.error("Failed to save country gateway assignment");
      }
    } finally {
      setSavingCountryGateway(false);
    }
  };

  const gatewayHeader: Column[] = [
    { id: "code", label: "Code", minWidth: 100, align: "left" },
    {
      id: "name",
      label: "Name",
      minWidth: 160,
      align: "left",
      format: (value: string, row: PaymentGateway) => {
        const isSupported = IMPLEMENTED_PROCESSORS.has(row.code.toUpperCase());
        return (
          <Space direction="vertical" size={2}>
            <span className="font-semibold">{value}</span>
            {!isSupported && (
              <Tooltip title="Cổng này chưa có backend processor/callback xử lý tự động trong hệ thống">
                <Tag color="warning" icon={<WarningOutlined />} style={{ fontSize: 11, margin: 0 }}>
                  Chưa có processor
                </Tag>
              </Tooltip>
            )}
          </Space>
        );
      },
    },
    { id: "type", label: "Type", minWidth: 120, align: "center" },
    {
      id: "is_active",
      label: "Status",
      minWidth: 100,
      align: "center",
      format: (value: boolean) => (
        <StatusBadge label={value ? "Active" : "Inactive"} tone={value ? "success" : "error"} />
      ),
    },
    {
      id: "is_default",
      label: "Default",
      minWidth: 100,
      align: "center",
      format: (value: boolean) => (value ? <StatusBadge label="Default" tone="info" /> : "-"),
    },
    { id: "display_order", label: "Order", minWidth: 80, align: "right" },
    {
      id: "id",
      label: "Actions",
      minWidth: 90,
      align: "center",
      format: (_: number, row: PaymentGateway) => (
        <IconButton size="small" color="success" onClick={() => openEditGateway(row)}>
          <EditOutlined />
        </IconButton>
      ),
    },
  ];

  const countryGatewayHeader: Column[] = [
    {
      id: "country_name",
      label: "Country",
      minWidth: 160,
      align: "left",
      format: (_: string, row: CountryPaymentGateway) => (
        <Space direction="vertical" size={0}>
          <span className="font-semibold">{row.country_name}</span>
          <Typography.Text type="secondary">{row.country_code}</Typography.Text>
        </Space>
      ),
    },
    {
      id: "gateway_name",
      label: "Gateway",
      minWidth: 170,
      align: "left",
      format: (_: string, row: CountryPaymentGateway) => {
        const isSupported = IMPLEMENTED_PROCESSORS.has(row.gateway_code.toUpperCase());
        return (
          <Space direction="vertical" size={0}>
            <Space size={4}>
              <span className="font-semibold">{row.gateway_name}</span>
              {!isSupported && (
                <Tooltip title="Chưa có processor IPN/callback trong backend">
                  <Tag color="warning" icon={<WarningOutlined />} style={{ fontSize: 10, margin: 0 }}>
                    Chưa có processor
                  </Tag>
                </Tooltip>
              )}
            </Space>
            <Typography.Text type="secondary">{row.gateway_code}</Typography.Text>
          </Space>
        );
      },
    },
    { id: "gateway_type", label: "Type", minWidth: 120, align: "center" },
    {
      id: "is_enabled",
      label: "Enabled",
      minWidth: 100,
      align: "center",
      format: (value: boolean) => (
        <StatusBadge label={value ? "Enabled" : "Disabled"} tone={value ? "success" : "error"} />
      ),
    },
    {
      id: "is_primary",
      label: "Primary",
      minWidth: 100,
      align: "center",
      format: (value: boolean) => (value ? <StatusBadge label="Primary" tone="info" /> : "-"),
    },
    { id: "display_order", label: "Order", minWidth: 80, align: "right" },
    {
      id: "id",
      label: "Actions",
      minWidth: 90,
      align: "center",
      format: (_: number, row: CountryPaymentGateway) => (
        <IconButton size="small" color="primary" onClick={() => openEditCountryGateway(row)}>
          <EditOutlined />
        </IconButton>
      ),
    },
  ];

  return (
    <Card className="user">
      {contextHolder}
      <Space direction="vertical" size={20} style={{ width: "100%" }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" gap={2}>
          <Typography.Title level={4} style={{ margin: 0 }}>
            Payment Gateway Config
          </Typography.Title>
          <CancelButton startIcon={<ReloadOutlined />} onClick={loadData} loading={loading}>
            Reload
          </CancelButton>
        </Box>

        <Box display="flex" justifyContent="space-between" alignItems="center" gap={2}>
          <Typography.Title level={5} style={{ margin: 0 }}>
            Gateways
          </Typography.Title>
          <ActionButton startIcon={<PlusOutlined />} onClick={openCreateGateway}>
            Add Gateway
          </ActionButton>
        </Box>
        <MuiTable header={gatewayHeader} data={gateways} isLoading={loading} />

        <Divider style={{ margin: 0 }} />

        <Box display="flex" justifyContent="space-between" alignItems="center" gap={2}>
          <Typography.Title level={5} style={{ margin: 0 }}>
            Country Assignments
          </Typography.Title>
          <ActionButton startIcon={<PlusOutlined />} onClick={openCreateCountryGateway}>
            Assign Gateway
          </ActionButton>
        </Box>
        <MuiTable header={countryGatewayHeader} data={countryGateways} isLoading={loading} />
      </Space>

      <Modal
        title="Payment Gateway"
        open={gatewayModalOpen}
        onCancel={() => setGatewayModalOpen(false)}
        footer={
          <ModalFooter onCancel={() => setGatewayModalOpen(false)} onSave={saveGateway} saving={savingGateway} />
        }
      >
        <Form form={gatewayForm} layout="vertical" initialValues={defaultGatewayValues}>
          <Form.Item noStyle shouldUpdate={(prev, cur) => prev.code !== cur.code}>
            {() => {
              const code = String(gatewayForm.getFieldValue("code") || "").toUpperCase();
              if (code && !IMPLEMENTED_PROCESSORS.has(code)) {
                return (
                  <Alert
                    type="warning"
                    showIcon
                    style={{ marginBottom: 16 }}
                    message="Cảnh báo: Cổng chưa có Processor Callback"
                    description={`Cổng ${code} chưa có processor callback/IPN trong backend (hiện chỉ STRIPE, VNPAY và SEPAY sẵn sàng). Kích hoạt có thể khiến thanh toán tự động không cập nhật trạng thái.`}
                  />
                );
              }
              return null;
            }}
          </Form.Item>
          <Form.Item name="code" label="Code" rules={[{ required: true, message: "Code is required" }]}>
            <InputField disabled={Boolean(editingGateway)} />
          </Form.Item>
          <Form.Item name="name" label="Name" rules={[{ required: true, message: "Name is required" }]}>
            <InputField />
          </Form.Item>
          <Form.Item name="type" label="Type" rules={[{ required: true, message: "Type is required" }]}>
            <SelectField options={gatewayTypes} searchable={false} matchAnchorWidth />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <InputField />
          </Form.Item>
          <Form.Item name="icon" label="Icon">
            <InputField />
          </Form.Item>
          <Form.Item name="checkout_url" label="Checkout URL">
            <InputField />
          </Form.Item>
          <Form.Item name="display_order" label="Display order">
            <InputField type="number" inputProps={{ min: 0 }} />
          </Form.Item>
          <Space size={24}>
            <Form.Item name="is_active" label="Active" valuePropName="checked">
              <Switch checkedChildren="ON" unCheckedChildren="OFF" />
            </Form.Item>
            <Form.Item name="is_default" label="Default fallback" valuePropName="checked">
              <Switch checkedChildren="ON" unCheckedChildren="OFF" />
            </Form.Item>
          </Space>
        </Form>
      </Modal>

      <Modal
        title="Country Gateway Assignment"
        open={countryGatewayModalOpen}
        onCancel={() => setCountryGatewayModalOpen(false)}
        footer={
          <ModalFooter
            onCancel={() => setCountryGatewayModalOpen(false)}
            onSave={saveCountryGateway}
            saving={savingCountryGateway}
          />
        }
      >
        <Form form={countryGatewayForm} layout="vertical" initialValues={defaultCountryGatewayValues}>
          <Form.Item noStyle shouldUpdate={(prev, cur) => prev.payment_gateway_id !== cur.payment_gateway_id}>
            {() => {
              const selectedId = countryGatewayForm.getFieldValue("payment_gateway_id");
              const selectedGateway = gateways.find((g) => g.id === selectedId);
              if (selectedGateway && !IMPLEMENTED_PROCESSORS.has(selectedGateway.code.toUpperCase())) {
                return (
                  <Alert
                    type="warning"
                    showIcon
                    style={{ marginBottom: 16 }}
                    message="Cảnh báo: Cổng chưa có processor callback"
                    description={`Cổng ${selectedGateway.name} (${selectedGateway.code}) chưa có processor xử lý IPN/callback trong backend. Kích hoạt cổng này cho quốc gia có thể khiến thanh toán tự động không thành công.`}
                  />
                );
              }
              return null;
            }}
          </Form.Item>
          <Form.Item name="country_id" label="Country" rules={[{ required: true, message: "Country is required" }]}>
            <SelectField options={countryOptions} />
          </Form.Item>
          <Form.Item
            name="payment_gateway_id"
            label="Payment gateway"
            rules={[{ required: true, message: "Payment gateway is required" }]}
          >
            <SelectField options={gatewayOptions} />
          </Form.Item>
          <Form.Item name="display_order" label="Display order">
            <InputField type="number" inputProps={{ min: 0 }} />
          </Form.Item>
          <Space size={24}>
            <Form.Item name="is_enabled" label="Enabled" valuePropName="checked">
              <Switch checkedChildren="ON" unCheckedChildren="OFF" />
            </Form.Item>
            <Form.Item name="is_primary" label="Primary" valuePropName="checked">
              <Switch checkedChildren="ON" unCheckedChildren="OFF" />
            </Form.Item>
          </Space>
        </Form>
      </Modal>
    </Card>
  );
};

export default PaymentGatewayConfig;
